"use client"
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const TeamIntroVisuals = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 6);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">Team Builder</span>
                    </div>
                </div>
                <div className="w-12" />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Basics key="step1" />}
                    {activeStep === 1 && <Step2_Members key="step2" />}
                    {activeStep === 2 && <Step3_QA key="step3" />}
                    {activeStep === 3 && <Step4_Outline key="step4" />}
                    {activeStep === 4 && <Step5_Theme key="step5" />}
                    {activeStep === 5 && <Step6_Edit key="step6" />}
                </AnimatePresence>

                {/* Progressive Step Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Company Context
const Step1_Basics = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center gap-4"
    >
        <div className="space-y-4 px-4 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-3xl mb-2">🚀</div>
            <div className="space-y-1">
                <div className="text-sm font-bold text-gray-900">Acme Inc.</div>
                <div className="text-xs text-gray-500">Series B &bull; 50-100 Employees</div>
            </div>
            <div className="text-xs text-blue-600 font-bold bg-blue-50 py-1.5 px-3 rounded-full inline-block">
                Leadership Team Deck
            </div>
        </div>
    </motion.div>
);

// Step 2: Team Members Input
const Step2_Members = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center"
    >
        <div className="space-y-3">
            <div className="text-xs font-bold text-gray-900 px-1">Core Team Members</div>
            {[
                { name: "Sarah Chen", role: "CTO", img: "bg-purple-100 text-purple-600" },
                { name: "Mike Ross", role: "VP Sales", img: "bg-blue-100 text-blue-600" },
                { name: "Jessica Wu", role: "Head of Product", img: "bg-pink-100 text-pink-600" }
            ].map((member, i) => (
                <motion.div
                    key={i}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.15 }}
                    className="flex items-center gap-3 p-2 border border-gray-100 rounded-xl bg-white shadow-sm"
                >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${member.img}`}>
                        {member.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                        <div className="text-xs font-bold text-gray-900">{member.name}</div>
                        <div className="text-[9px] text-gray-500">{member.role}</div>
                    </div>
                    <div className="w-4 h-4 text-green-500">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 3: Q&A
const Step3_QA = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-3 justify-center"
    >
        {/* AI Question */}
        <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex gap-3 items-start"
        >
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 shadow-lg shadow-blue-200">AI</div>
            <div className="bg-gray-100 rounded-2xl rounded-tl-none p-3 text-xs text-gray-800 leading-relaxed">
                What is Sarah's key achievement as CTO?
            </div>
        </motion.div>

        {/* User Answer */}
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex gap-3 items-start flex-row-reverse"
        >
            <div className="bg-blue-600 text-white rounded-2xl rounded-tr-none p-3 text-xs leading-relaxed shadow-md">
                Led the migration to microservices, reducing costs by 40%...
                <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block w-0.5 h-3 bg-white align-middle ml-1"
                />
            </div>
        </motion.div>
    </motion.div>
);

// Step 4: Outline
const Step4_Outline = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff]"
    >
        <div className="flex items-center gap-2 mb-4">
            <div className="h-1 w-8 bg-blue-600 rounded-full" />
            <span className="text-xs font-bold text-gray-900">Structure</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
            {[
                "Team Overview",
                "Leadership Bios",
                "Advisory Board",
                "Org Structure"
            ].map((item, i) => (
                <motion.div
                    key={i}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="aspect-video bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center relative overflow-hidden group hover:border-blue-200 transition-colors"
                >
                    <div className="text-[9px] font-bold text-gray-500 group-hover:text-blue-600 transition-colors">{item}</div>
                    <div className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-green-500 rounded-full" />
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 5: Theme
const Step5_Theme = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-4"
    >
        <div className="flex justify-between items-center text-xs font-bold text-gray-900 border-b border-gray-50 pb-2">
            <span>Select Style</span>
            <div className="px-2 py-0.5 bg-gray-100 rounded-full text-[9px] text-gray-500">Modern</div>
        </div>

        <div className="h-full relative px-2">
            {/* Main Card */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="w-full aspect-[16/9] bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex gap-3 items-center"
            >
                <div className="w-16 h-16 bg-gray-100 rounded-full border-2 border-white shadow-sm flex-shrink-0" />
                <div className="flex-1 space-y-2">
                    <div className="h-2 w-1/2 bg-gray-900 rounded" />
                    <div className="h-1.5 w-1/3 bg-blue-600 rounded" />
                    <div className="h-1 w-full bg-gray-100 rounded mt-2" />
                    <div className="h-1 w-full bg-gray-100 rounded" />
                </div>
            </motion.div>

            {/* Color Swatches */}
            <div className="flex justify-center gap-2 mt-4">
                <div className="w-4 h-4 rounded-full bg-gray-900 border-2 border-white shadow-sm ring-1 ring-gray-200" />
                <div className="w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow-sm ring-1 ring-gray-200 scale-125" />
                <div className="w-4 h-4 rounded-full bg-white border-2 border-white shadow-sm ring-1 ring-gray-200" />
            </div>
        </div>
    </motion.div>
);

// Step 6: Edit
const Step6_Edit = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full"
    >
        <div className="flex-1 bg-white border border-gray-100 rounded-xl shadow-sm p-4 relative overflow-hidden flex flex-col items-center text-center">
            <motion.div
                layoutId="avatar"
                className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 mb-3 shadow-lg border-2 border-white"
            />

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="space-y-1 mb-3 w-full"
            >
                <div className="h-3 w-1/3 bg-gray-900 rounded mx-auto" />
                <div className="h-2 w-1/4 bg-blue-500 rounded mx-auto" />
            </motion.div>

            <div className="w-full space-y-1.5 opacity-60">
                <div className="h-1.5 w-3/4 bg-gray-200 rounded mx-auto" />
                <div className="h-1.5 w-5/6 bg-gray-200 rounded mx-auto" />
                <div className="h-1.5 w-2/3 bg-gray-200 rounded mx-auto" />
            </div>

            {/* Bio Generator Overlay */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1 }}
                className="absolute inset-x-4 bottom-4 bg-gray-900/90 backdrop-blur text-white p-2 rounded-lg flex items-center gap-2 shadow-xl"
            >
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                <span className="text-[9px] font-bold">AI writing bio...</span>
            </motion.div>
        </div>
    </motion.div>
);
